// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import ConfettiExplosion from "react-confetti-explosion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Header from "./components/layout/Header";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import FormPage from "./pages/FormPage";
import PreviewPage from "./pages/PreviewPage";
import WorkoutPage from "./pages/WorkoutPage";
import CompletePage from "./pages/CompletePage";
import LibraryPage from "./pages/LibraryPage";
import ExplorePage from "./pages/ExplorePage";
import ExerciseDetail from "./pages/ExerciseDetail";
import CustomWorkoutBuilder from "./pages/CustomWorkoutBuilder";
import AnimationDemo from "./components/animations/AnimationDemo";
import ScrollToTop from "./components/layout/ScrollToTop";

import { auth } from "./config/firebase";
import { saveWorkout } from "./utils/saveWorkout";
import { onAuthStateChanged, signOut } from "firebase/auth";
import useMetaTags from "./utils/useMetaTags";
import { MUSCLE_GROUP_MAP } from "./utils/muscleGroups";
import { generateSmartWorkout } from "./utils/workoutGeneration";
import { useAuth } from "./hooks/useAuth";
import { useWorkout } from "./hooks/useWorkout";
import { useLocalStorage } from "./hooks/useLocalStorage";

import exercisesData from "./data/exercises.json";

function AppContent() {
  console.log('=== AppContent function called ===');
  const allExercises = exercisesData;
  const location = useLocation();
  
  const [generationError, setGenerationError] = useState("");
  // Use custom localStorage hooks
  const [selectedGroups, setSelectedGroups] = useLocalStorage("selectedGroups", []);
  const [exerciseCount, setExerciseCount] = useLocalStorage("exerciseCount", 5);
  const [selectedCategory, setSelectedCategory] = useLocalStorage("selectedCategory", null);
  const [selectedLevel, setSelectedLevel] = useLocalStorage("selectedLevel", null);
  // Use custom workout hook
  const {
    workout,
    setWorkout,
    workoutName,
    setWorkoutName,
    currentStep,
    setCurrentStep,
    customWorkoutData,
    setCustomWorkoutData,
    fromLibrary,
    setFromLibrary,
    handleNext,
    handlePrevious,
    handleRestart,
    handleRepeatWorkout
  } = useWorkout(allExercises);

  // Process custom workout data when workout page loads
  useEffect(() => {
    console.log('=== useEffect triggered ===');
    console.log('Current pathname:', location.pathname);
    console.log('Custom workout data in localStorage:', localStorage.getItem('customWorkout'));
    console.log('isCustomWorkout flag:', localStorage.getItem('isCustomWorkout'));
    console.log('useEffect is running with pathname:', location.pathname);
    
    // Simple test to see if useEffect is running
    // alert('useEffect is running!');
    
    // Check if this is a custom workout and we're on the workout route
    if (location.pathname === '/workout') {
      console.log('=== ON WORKOUT ROUTE ===');
      console.log('isCustomWorkout flag:', localStorage.getItem('isCustomWorkout'));
      if (localStorage.getItem('isCustomWorkout') === 'true') {
        console.log('=== CUSTOM WORKOUT DETECTED ===');
        const customWorkoutData = localStorage.getItem('customWorkout');
        if (customWorkoutData) {
          console.log('=== Processing custom workout data on workout page load ===');
          console.log('Custom workout data from localStorage:', customWorkoutData);
          
          try {
            const customWorkout = JSON.parse(customWorkoutData);
            console.log('Custom workout data:', customWorkout);
            console.log('Custom workout exercises:', customWorkout.exercises);
            
            // Ensure exercises have full data including repRange
            const enrichedExercises = customWorkout.exercises.map(exercise => {
              const fullExercise = allExercises.find(ex => ex.id === exercise.id);
              console.log('Enriching exercise:', exercise.name, 'with full data:', fullExercise);
              return fullExercise || exercise;
            });
            
            console.log('Enriched exercises:', enrichedExercises);
            
            // Clear any existing workout data first
            localStorage.removeItem('workout');
            localStorage.removeItem('workoutName');
            
            // Set custom workout data for immediate use
            setCustomWorkoutData(enrichedExercises);
            setWorkoutName(customWorkout.name);
            // Persist to localStorage
            localStorage.setItem("workout", JSON.stringify(enrichedExercises));
            localStorage.setItem("workoutName", customWorkout.name);
            // Reset current step to 0 for custom workout
            setCurrentStep(0);
            localStorage.setItem("currentStep", "0");
            // Clear custom workout data and flag
            localStorage.removeItem('customWorkout');
            localStorage.removeItem('isCustomWorkout');
            
            console.log('Custom workout loaded and persisted');
          } catch (error) {
            console.error('Error parsing custom workout:', error);
          }
        }
      }
    }
  }); // Run on every render
  const [isExploding, setIsExploding] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  
  const navigate = useNavigate();
  // Use custom auth hook
  const { user, setUser, handleLogout, loading: authLoading } = useAuth(navigate);



  // Ensure workout exercises have full data including repRange
  useEffect(() => {
    if (workout.length > 0) {
      const updatedWorkout = workout.map(exercise => {
        const fullExercise = allExercises.find(ex => ex.id === exercise.id);
        return fullExercise || exercise;
      });
      
      // Only update if there are changes
      const hasChanges = updatedWorkout.some((ex, index) => 
        JSON.stringify(ex) !== JSON.stringify(workout[index])
      );
      
      if (hasChanges) {
        setWorkout(updatedWorkout);
        localStorage.setItem("workout", JSON.stringify(updatedWorkout));
      }
    }
  }, [workout, allExercises]);

  const muscleGroupMap = MUSCLE_GROUP_MAP;

  const getRandomWorkout = () => {
    if (selectedGroups.length === 0) {
      alert("Please select at least one muscle group.");
      return [];
    }

    // Use smart workout generation with practical constraints
    const criteria = {
      muscleGroups: selectedGroups,
      category: selectedCategory,
      level: selectedLevel,
      equipment: null // Let the algorithm determine equipment compatibility
    };

    return generateSmartWorkout(criteria, exerciseCount);
  };

  const handleGenerate = () => {
    const newWorkout = getRandomWorkout();

    if (newWorkout.length === 0) {
      setGenerationError("There are not enough exercises in the database that match your criteria. Try adjusting your filters.");
      return;
    }


    const today = new Date();
    const month = today.toLocaleString("default", { month: "long" });
    const day = today.getDate();
    const generatedName = `${selectedGroups
      .map((group) => group.charAt(0).toUpperCase() + group.slice(1))
      .join(", ")} Workout on ${month} ${day}`;

    setWorkoutName(generatedName);
    setFromLibrary(false);
    setWorkout(newWorkout);
    setGenerationError("");
    navigate("/preview");
  };

  // 🔹 FIXED shuffle logic
  const handleSwap = (index) => {
    const allowedMuscles = selectedGroups.flatMap((group) => muscleGroupMap[group] || []);

    const alternatives = allExercises.filter((ex) => {
      const matchesMuscle =
        ex.primaryMuscles.concat(ex.secondaryMuscles).some((muscle) => allowedMuscles.includes(muscle));
      const matchesCategory = selectedCategory ? ex.category?.toLowerCase() === selectedCategory.toLowerCase() : true;
      const matchesLevel = selectedLevel ? ex.level?.toLowerCase() === selectedLevel.toLowerCase() : true;
      const notInWorkout = !workout.some((w) => w.id === ex.id);
      return matchesMuscle && matchesCategory && matchesLevel && notInWorkout;
    });

    if (alternatives.length > 0) {
      const replacement = alternatives[Math.floor(Math.random() * alternatives.length)];
      const newWorkout = [...workout];
      newWorkout[index] = replacement;
      setWorkout(newWorkout);
      localStorage.setItem("workout", JSON.stringify(newWorkout)); // persist shuffle
    }
  };

  const handleStart = () => {
    console.log('=== handleStart called ===');
    console.log('Current workout state before:', workout);
    console.log('Current workoutName before:', workoutName);
    
    // Check for custom workout data in localStorage
    const customWorkoutFromStorage = localStorage.getItem('customWorkout');
    const isCustomWorkoutFlag = localStorage.getItem('isCustomWorkout');
    console.log('isCustomWorkout flag in handleStart:', isCustomWorkoutFlag);
    console.log('customWorkoutFromStorage:', customWorkoutFromStorage);
    
    if (customWorkoutFromStorage && isCustomWorkoutFlag === 'true') {
      console.log('=== Processing custom workout data in handleStart ===');
      console.log('Custom workout data from localStorage:', customWorkoutFromStorage);
      
      try {
        const customWorkout = JSON.parse(customWorkoutFromStorage);
        console.log('Custom workout data:', customWorkout);
        console.log('Custom workout exercises:', customWorkout.exercises);
        
        // Ensure exercises have full data including repRange
        const enrichedExercises = customWorkout.exercises.map(exercise => {
          const fullExercise = allExercises.find(ex => ex.id === exercise.id);
          console.log('Enriching exercise:', exercise.name, 'with full data:', fullExercise);
          return fullExercise || exercise;
        });
        
        console.log('Enriched exercises:', enrichedExercises);
        
        // Clear any existing workout data first
        localStorage.removeItem('workout');
        localStorage.removeItem('workoutName');
        
        // Set custom workout data for immediate use
        setCustomWorkoutData(enrichedExercises);
        setWorkoutName(customWorkout.name);
        // Persist to localStorage
        localStorage.setItem("workout", JSON.stringify(enrichedExercises));
        localStorage.setItem("workoutName", customWorkout.name);
        // Reset current step to 0 for custom workout
        setCurrentStep(0);
        localStorage.setItem("currentStep", "0");
        // Clear custom workout data and flag
        localStorage.removeItem('customWorkout');
        localStorage.removeItem('isCustomWorkout');
        
        console.log('Custom workout loaded and persisted in handleStart');
        
        // Navigate to workout
        navigate("/workout");
        return;
      } catch (error) {
        console.error('Error parsing custom workout:', error);
      }
    }
    
    if (workout.length === 0) {
      alert("No workout to start!");
      return;
    }
    setCurrentStep(0);
    navigate("/workout");
  };


  // 🔹 UPDATED Save Workout logic
  const handleSaveWorkout = async () => {
    if (!user) {
      // save current page before login
      localStorage.setItem("pendingBookmark", JSON.stringify({ returnUrl: window.location.pathname }));
      // Also set a flag to indicate save workout action was intended
      localStorage.setItem("pendingBookmarkAction", JSON.stringify({
        action: "saveWorkout"
      }));
      navigate("/login");
      return;
    }

    if (!workoutName) {
      alert("Please enter a workout name.");
      return;
    }

    try {
      await saveWorkout({ user, name: workoutName, exercises: workout });
      toast.success("Workout saved to your library!");
      setWorkoutName("");
      navigate("/library");
    } catch (err) {
      console.error("Error saving workout:", err);
      toast.error("Failed to save workout. Try again.");
    }
  };



  useEffect(() => {
    if (window.location.pathname === "/complete") {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000);
    }
  }, []);

  // Dynamic meta tags based on current route
  const getCurrentMetaTags = () => {
    const path = window.location.pathname;
    const baseUrl = "https://gymmix.app";
    
    if (path === "/explore") {
      return {
        title: "Explore Exercises - GymMix",
        description: "Discover exercises by muscle group, category, and difficulty level. Find the perfect exercises for your fitness goals.",
        image: `${baseUrl}/og-image.svg`,
        url: `${baseUrl}${path}`
      };
    } else if (path.startsWith("/exercise/")) {
      const exerciseId = path.split("/")[2];
      const exercise = allExercises.find(ex => ex.id === exerciseId);
      if (exercise) {
        return {
          title: `${exercise.name} - Exercise Guide | GymMix`,
          description: `${exercise.name} exercise guide with instructions, muscle groups, and training tips. ${exercise.category} • ${exercise.level}`,
          image: exercise.images && exercise.images.length > 0 ? `${baseUrl}/exercise-images/${exercise.images[0]}` : `${baseUrl}/og-image.jpg`,
          url: `${baseUrl}${path}`
        };
      }
    } else if (path === "/library") {
      return {
        title: "My Library - GymMix",
        description: "Your saved exercises and workouts. Access your personalized fitness library anytime.",
        image: `${baseUrl}/og-image.svg`,
        url: `${baseUrl}${path}`
      };
    } else if (path === "/form") {
      return {
        title: "Generate Workout - GymMix",
        description: "Create a personalized workout based on your preferences. Select muscle groups, difficulty, and equipment.",
        image: `${baseUrl}/og-image.svg`,
        url: `${baseUrl}${path}`
      };
    }
    
    // Default meta tags
    return {
      title: "GymMix - Your Personal Fitness Companion",
      description: "Discover exercises, generate personalized workouts, and track your fitness journey with GymMix.",
      image: `${baseUrl}/og-image.jpg`,
      url: `${baseUrl}${path}`
    };
  };

  const metaTags = getCurrentMetaTags();
  useMetaTags(metaTags);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <Header user={user} onLogout={handleLogout} />
      <ScrollToTop />

      <Routes>
        <Route path="/login" element={<LoginPage setView={async () => {
          // Check for pending bookmark first, otherwise go to form
          const pendingBookmark = localStorage.getItem("pendingBookmark");
          if (pendingBookmark) {
            const { returnUrl } = JSON.parse(pendingBookmark);
            localStorage.removeItem("pendingBookmark");
            navigate(returnUrl);
          } else {
            // Check for pending bookmark action (for custom workouts)
            const pendingAction = localStorage.getItem("pendingBookmarkAction");
            if (pendingAction) {
              const { action } = JSON.parse(pendingAction);
              if (action === 'saveCustomWorkout') {
                // Just set flags and navigate - let the CustomWorkoutBuilder handle the save
                localStorage.setItem('showWorkoutsTab', 'true');
                localStorage.setItem('highlightNewWorkout', 'Custom Workout');
                navigate("/custom-workout");
                return;
                // Fallback: just set flags and navigate
                localStorage.setItem('showWorkoutsTab', 'true');
                localStorage.setItem('highlightNewWorkout', 'Custom Workout');
                navigate("/library");
                return;
              }
            }
            navigate("/form");
          }
        }} />} />
        <Route path="/signup" element={<SignupPage setView={async () => {
          // Check for pending bookmark first, otherwise go to form
          const pendingBookmark = localStorage.getItem("pendingBookmark");
          if (pendingBookmark) {
            const { returnUrl } = JSON.parse(pendingBookmark);
            localStorage.removeItem("pendingBookmark");
            navigate(returnUrl);
          } else {
            // Check for pending bookmark action (for custom workouts)
            const pendingAction = localStorage.getItem("pendingBookmarkAction");
            if (pendingAction) {
              const { action } = JSON.parse(pendingAction);
              if (action === 'saveCustomWorkout') {
                // Just set flags and navigate - let the CustomWorkoutBuilder handle the save
                localStorage.setItem('showWorkoutsTab', 'true');
                localStorage.setItem('highlightNewWorkout', 'Custom Workout');
                navigate("/custom-workout");
                return;
                // Fallback: just set flags and navigate
                localStorage.setItem('showWorkoutsTab', 'true');
                localStorage.setItem('highlightNewWorkout', 'Custom Workout');
                navigate("/library");
                return;
              }
            }
            navigate("/form");
          }
        }} />} />
        <Route path="/form" element={<FormPage {...{ selectedGroups, setSelectedGroups, exerciseCount, setExerciseCount, selectedCategory, setSelectedCategory, selectedLevel, setSelectedLevel, handleGenerate, generationError }} />} />
        <Route path="/preview" element={<PreviewPage exercises={workout} setExercises={setWorkout} handleStart={handleStart} handleSwap={handleSwap} user={user} selectedMuscles={selectedGroups} handleSaveWorkout={handleSaveWorkout} />} />
        <Route path="/workout" element={
          (customWorkoutData || workout)?.[currentStep] ? (
            <WorkoutPage 
              exercise={(customWorkoutData || workout)[currentStep]} 
              handleNext={() => handleNext(navigate, setIsExploding)} 
              handlePrevious={handlePrevious} 
              currentStep={currentStep} 
              totalSteps={(customWorkoutData || workout).length} 
              selectedMuscles={selectedGroups} 
              workoutName={workoutName} 
            />
          ) : (
            <div className="space-y-6 max-w-3xl mx-auto">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-700">Loading workout...</h2>
                <p className="text-gray-500">Please wait while we load your workout.</p>
              </div>
            </div>
          )
        } />
        <Route path="/complete" element={<CompletePage fromLibrary={fromLibrary} handleRestart={handleRestart} handleSaveWorkout={handleSaveWorkout} user={user} workout={workout} workoutName={workoutName} />} />
        <Route path="/library" element={<LibraryPage user={user} workoutName={workoutName} onRepeatWorkout={handleRepeatWorkout} />} />
        <Route path="/" element={<Navigate to="/explore" />} />
        <Route path="*" element={<Navigate to={user ? "/form" : "/explore"} />} />
        <Route path="/explore" element={<ExplorePage user={user} />} />
        <Route path="/exercise/:id" element={<ExerciseDetail user={user} />} />
        <Route path="/custom-workout" element={<CustomWorkoutBuilder />} />
        <Route path="/animation-demo" element={<AnimationDemo />} />
      </Routes>

      <ToastContainer
        position="top-center"
        hideProgressBar
        toastStyle={{
          backgroundColor: "#e6ffed",
          color: "#1a1a1a",
          borderRadius: "8px",
          padding: "12px 16px",
          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
        }}
      />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}
